import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import {
  PROFILE_CONFIG_TAG_TYPE,
  PROFILE_WIDGETS_TAG_TYPE,
  ALL_CONFIGS_ID,
  FEEDBACK_TAG_TYPE,
} from './constants';
import type {
  ProfileWidget,
  ProfileConfig,
  WidgetConfig,
  CreateProfileConfigParams,
  SaveProfileConfigParams,
  EntityProfileConfig,
  GetEntityProfileConfigParams,
  EntityPlaysResponse,
  MatchedPlay,
  FeedbackListResponse,
  FeedbackItem,
  CreateFeedbackItemParams,
  GetFeedbackListParams,
  ExecuteProfileEnrichmentParams,
} from './types';

export interface ProfileConfigsRegenerationJob {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export const profileConfigurationApi = createApi({
  reducerPath: 'profileConfigurationApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [
    PROFILE_CONFIG_TAG_TYPE,
    PROFILE_WIDGETS_TAG_TYPE,
    FEEDBACK_TAG_TYPE,
  ],
  endpoints: (builder) => ({
    // GET /api/v1/accounts/profile-configs/widgets/
    getWidgetDefinitions: builder.query<{ results: ProfileWidget[] }, void>({
      query: () => 'v1/accounts/profile-configs/widgets/',
      providesTags: [{ type: PROFILE_WIDGETS_TAG_TYPE, id: 'ALL' }],
    }),

    // GET /api/v1/accounts/profile-configs/
    listProfileConfigs: builder.query<ProfileConfig, void>({
      query: () => 'v1/accounts/profile-configs/',
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map((config) => ({
                type: PROFILE_CONFIG_TAG_TYPE,
                id: config.id.toString(),
              })),
              { type: PROFILE_CONFIG_TAG_TYPE, id: ALL_CONFIGS_ID },
            ]
          : [{ type: PROFILE_CONFIG_TAG_TYPE, id: ALL_CONFIGS_ID }],
    }),

    // GET v1/entities/entity/<entity_id>/profile-config/
    getProfileConfigV2: builder.query<EntityProfileConfig, GetEntityProfileConfigParams>({
      query: ({ entityId }) => `v1/entities/entity/${entityId}/profile-config/`,
      providesTags: (_result, _error, { entityId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: `entity-${entityId}` },
      ],
    }),
    // GET /api/v1/accounts/profile-configs/<config_id>/
    getProfileConfig: builder.query<WidgetConfig, { configId: number }>({
      query: ({ configId }) => `v1/accounts/profile-configs/${configId}/`,
      providesTags: (_result, _error, { configId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: configId.toString() },
      ],
    }),
    // POST /api/v1/accounts/profile-configs/
    createProfileConfig: builder.mutation<
      WidgetConfig,
      CreateProfileConfigParams
    >({
      query: ({ entity_type, widgets }) => ({
        url: 'v1/accounts/profile-configs/',
        method: 'POST',
        body: { entity_type, widgets },
      }),
      invalidatesTags: [{ type: PROFILE_CONFIG_TAG_TYPE, id: ALL_CONFIGS_ID }],
    }),

    // PUT /api/v1/accounts/profile-configs/<config_id>/
    saveProfileConfig: builder.mutation<WidgetConfig, SaveProfileConfigParams>({
      query: ({ configId, entity_type, widgets, triggers }) => ({
        url: `v1/accounts/profile-configs/${configId}/`,
        method: 'PUT',
        body: {
          entity_type,
          widgets,
          ...(triggers !== undefined && { triggers }),
        },
      }),
      invalidatesTags: (_result, _error, { configId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: configId.toString() },
        { type: PROFILE_CONFIG_TAG_TYPE, id: ALL_CONFIGS_ID },
      ],
    }),

    // GET /api/v1/entities/<entity_id>/profile-config/
    getEntityProfileConfig: builder.query<
      EntityProfileConfig,
      GetEntityProfileConfigParams
    >({
      query: ({ entityId }) => `v1/entities/entity/${entityId}/profile-config/`,
      providesTags: (_result, _error, { entityId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: `entity-${entityId}` },
      ],
    }),

    // GET /api/v1/entities/entity/<entity_id>/plays/
    getEntityPlays: builder.query<
      EntityPlaysResponse,
      { entityId: string | number }
    >({
      query: ({ entityId }) => `v1/entities/entity/${entityId}/plays/`,
      transformResponse: (
        response: EntityPlaysResponse | MatchedPlay[]
      ): EntityPlaysResponse =>
        Array.isArray(response)
          ? { count: response.length, results: response }
          : response,
      providesTags: (_result, _error, { entityId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: `entity-plays-${entityId}` },
      ],
    }),

    // Feedback apis
    getFeedbackList: builder.query<FeedbackListResponse, GetFeedbackListParams>(
      {
        query: ({ serviceCode }) => ({
          url: `v1/enrichments/feedback/${serviceCode}/`,
        }),
        providesTags: [{ type: FEEDBACK_TAG_TYPE, id: 'LIST' }],
      }
    ),

    createFeedbackItem: builder.mutation<
      FeedbackItem,
      CreateFeedbackItemParams
    >({
      query: ({ configId, serviceCode, feedback, entityType }) => ({
        url: `v1/enrichments/feedback/${serviceCode}/`,
        method: 'POST',
        body: { feedback, config_id: configId, entity_type: entityType },
      }),
      invalidatesTags: [{ type: FEEDBACK_TAG_TYPE, id: 'LIST' }],
    }),

    deleteFeedbackItem: builder.mutation<void, { feedbackId: string }>({
      query: ({ feedbackId }) => ({
        url: `v1/enrichments/feedback/${feedbackId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: FEEDBACK_TAG_TYPE, id: 'LIST' }],
    }),

    // POST /api/v1/accounts/profile-configs/regenerate/
    regenerateProfileConfigs: builder.mutation<ProfileConfigsRegenerationJob, void>({
      query: () => ({
        url: 'v1/accounts/profile-configs/regenerate/',
        method: 'POST',
      }),
    }),

    // GET /api/v1/accounts/profile-configs/regenerate/
    getProfileConfigsRegenerationJobs: builder.query<
      { count: number; results: ProfileConfigsRegenerationJob[] },
      void
    >({
      query: () => 'v1/accounts/profile-configs/regenerate/',
    }),

    // GET /api/v1/accounts/profile-configs/regenerate/<id>/
    getProfileConfigsRegenerationJob: builder.query<ProfileConfigsRegenerationJob, number>({
      query: (jobId) => `v1/accounts/profile-configs/regenerate/${jobId}/`,
    }),

    // POST /api/v1/enrichments/{serviceCode}/execute/
    executeEnrichment: builder.mutation<void, ExecuteProfileEnrichmentParams>({
      query: ({ serviceCode, entityType }) => ({
        url: `v1/enrichments/${serviceCode}/execute/`,
        method: 'POST',
        body: {
          force: true,
          entity_types: [entityType],
        },
      }),
      invalidatesTags: (_result, _error, { configId, entityId }) => {
        const tags: { type: typeof PROFILE_CONFIG_TAG_TYPE; id: string }[] = [
          { type: PROFILE_CONFIG_TAG_TYPE, id: configId.toString() },
        ];
        if (entityId) {
          tags.push({
            type: PROFILE_CONFIG_TAG_TYPE,
            id: `entity-${entityId}`,
          });
        }
        return tags;
      },
    }),

    // POST /api/v1/enrichments/generate_talk_track/execute/
    // entityId is not sent to the backend — it's only used for cache invalidation
    generateTalkTrack: builder.mutation<
      { talk_track: string },
      { providerId: string | number; playId: number; entityId: string | number }
    >({
      query: ({ providerId, playId }) => ({
        url: 'v1/enrichments/generate_talk_track/execute/',
        method: 'POST',
        body: {
          provider_id: providerId,
          play_id: playId,
        },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [
        { type: PROFILE_CONFIG_TAG_TYPE, id: `entity-plays-${entityId}` },
      ],
    }),
  }),
});

export const {
  useGetWidgetDefinitionsQuery,
  useListProfileConfigsQuery,
  useGetProfileConfigQuery,
  useGetProfileConfigV2Query,
  useCreateProfileConfigMutation,
  useSaveProfileConfigMutation,
  useGetEntityProfileConfigQuery,
  useGetEntityPlaysQuery,
  useRegenerateProfileConfigsMutation,
  useGetProfileConfigsRegenerationJobsQuery,
  useGetProfileConfigsRegenerationJobQuery,
  // Feedback hooks
  useGetFeedbackListQuery,
  useCreateFeedbackItemMutation,
  useDeleteFeedbackItemMutation,
  // Enrichment hooks
  useExecuteEnrichmentMutation,
  useGenerateTalkTrackMutation,
} = profileConfigurationApi;
